import {
  Driver,
  objectIdSchema,
  paginationWithSearchSchema,
  driverStatusSchema,
} from '@hero/schema';
import { API } from '..';
import { z } from 'zod';

//* get one
export const driverGetOneAPISchema = z.object({
  _id: objectIdSchema,
});

export type DriverGetOneAPI = API<z.infer<typeof driverGetOneAPISchema>, Driver>;

// * get all by carrier
export const driverCarrierListStatusFilterSchema = z
  .union([driverStatusSchema, z.literal('no_vehicle')])
  .describe(
    "Driver list status filter. Values from `driverStatusSchema` map directly to `Driver.status`. 'no_vehicle' is a virtual filter — drivers with no vehicle assigned, regardless of status.",
  );

/**
 * Filter-only schema for the carrier driver list query.
 *
 * Same role as {@link ordersQueryAPISchema} / {@link vehiclesQueryAPISchema} —
 * carries the **filter** vocabulary (no pagination, no search) so agent
 * tools and analytics callers can compose from it without inheriting
 * request-level surface. {@link driverGetAllCarrierAPISchema} extends
 * this with `paginationWithSearchSchema` for the existing public API.
 *
 * Mapped to MongoDB by `DriverQueryService.transformDriversQuery`.
 */
export const driversQueryAPISchema = z.object({
  status: driverCarrierListStatusFilterSchema.optional(),
});
export type DriversQueryAPI = z.infer<typeof driversQueryAPISchema>;

/** Request schema for `GET /driver/carrier` — filters + pagination + search. */
export const driverGetAllCarrierAPISchema = paginationWithSearchSchema.extend({
  ...driversQueryAPISchema.shape,
});
export type DriverGetAllCarrierAPI = API<z.infer<typeof driverGetAllCarrierAPISchema>, Driver[]>;

// === ADMIN APIs ===

//* get all admin
export const driverGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** the carrier id to get the drivers for. If empty then we will return all drivers paginated */
  orgId: objectIdSchema.optional(),
});
export type DriverGetAllAdminAPI = API<z.infer<typeof driverGetAllAdminAPISchema>, Driver[]>;

//* update admin (enhanced for status and future fields)
export const driverUpdateAdminAPISchema = z.object({
  _id: objectIdSchema,
  status: driverStatusSchema.optional(),
  // Future fields can be added here (e.g., name, phone, etc.)
});
export type DriverUpdateAdminAPI = API<z.infer<typeof driverUpdateAdminAPISchema>, Driver>;

//* delete admin (dummy)
export const driverDeleteAPISchema = z.object({
  _id: objectIdSchema,
});
export type DriverDeleteAPI = API<z.infer<typeof driverDeleteAPISchema>, { success: boolean }>;
